import * as DialogPrimitive from '@radix-ui/react-dialog';
import React, { useCallback, useEffect, useState } from 'react';
import './ProductOptionEditor.css';
import { ProductOptionInput } from '@shopify-multiseller/graphql/partner/types';
import { Alert } from '../primitives';
import { Button } from '../primitives';
import { Input, Label } from '../primitives';
import { v4 as uuid } from 'uuid';
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TrashIcon } from '@heroicons/react/24/solid';
import { GripVertical } from 'lucide-react';

type ValueObject = {
    id: string;
    value: string;
}

type ProductOption = {
    id: string;
    name: string;
    values: ValueObject[];
}

const SortableValueRow: React.FC<{
    value: ValueObject;
    error: boolean;
    onChange: (_id: string, _value: string) => void;
    onDelete: (_id: string) => void;
}> = ({ value, error, onChange, onDelete }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: String(value.id) });
    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition, zIndex: isDragging ? 50 : undefined }}
            className="flex flex-row items-center mt-1 mb-1 space-x-4"
        >
            <div {...attributes} {...listeners}>
                <GripVertical className="w-5 h-5 text-gray-500" />
            </div>
            <div className="flex-grow">
                <Input
                    aria-label="Value"
                    value={value.value}
                    error={error}
                    onChange={e => onChange(value.id, e.target.value)}
                />
            </div>
            <div>
                <button onClick={() => onDelete(value.id)}>
                    <span className="sr-only">Delete value</span>
                    <TrashIcon className="w-5 h-5 text-gray-500" aria-hidden="true" />
                </button>
            </div>
        </div>
    );
};

export interface IProductOptionEditorProps {
    open?: boolean;
    onClose: () => void;
    onSave: (_option: ProductOptionInput) => void;
    option?: ProductOptionInput;
    options: ProductOptionInput[];
}

/**
 * Add/edit dialog for a product option (name + ordered values). Runs on a
 * Radix Dialog (focus trap/Escape/aria) with the legacy CSS layout, and
 * primitives Input/Label instead of the legacy Input. Emitting the option
 * PRESERVES its position (the old code hardcoded position: 0, so editing an
 * option silently moved it to the front); new options append after the
 * existing ones.
 */
export const ProductOptionEditor: React.FC<React.PropsWithChildren<IProductOptionEditorProps>> = ({
    open = false,
    onClose,
    onSave,
    option,
    options
}) => {
    const defaultProductOption = { id: uuid(), name: "", values: [] };

    const [modified, setModified] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<string | undefined>(undefined);
    const [errorValues, setErrorValues] = useState<Set<string>>(new Set());

    const [valueInput, setValueInput] = useState<string>("");
    const [productOption, setProductOption] = useState<ProductOption>(defaultProductOption);
    const [originalProductOption, setOriginalProductOption] = useState<ProductOption>(defaultProductOption);

    const [nameExists, setNameExists] = useState<boolean>(false);

    useEffect(() => {
        if (open) {
            setValidationError(undefined);
            setModified(false);
            if (option) {
                setOriginalProductOption({ id: option.id, name: option.name, values: option.values.map(v => ({ id: uuid(), value: v })) });
            } else {
                setOriginalProductOption(defaultProductOption);
            }
        }
    }, [open, option]);

    useEffect(() => {
        setProductOption(originalProductOption);
    }, [originalProductOption]);

    useEffect(() => {
        if (JSON.stringify(originalProductOption) !== JSON.stringify(productOption)) {
            setModified(true);
        }
        const valueCounts = new Map<string, number>();
        productOption.values.forEach(valueObj => {
            const count = valueCounts.get(valueObj.value) || 0;
            valueCounts.set(valueObj.value, count + 1);
        });

        const duplicates = Array.from(valueCounts.entries()).filter(([, count]) => count > 1).map(([value]) => value);
        if (duplicates.length > 0) {
            setValidationError("Please resolve duplicate values before saving.");
            setErrorValues(new Set(duplicates));
        } else {
            setValidationError(undefined);
            setErrorValues(new Set());
        }
    }, [productOption]);

    useEffect(() => {
        const storedOption = options?.find(o => o.id === productOption.id);

        if (storedOption?.name === productOption.name) {
            setNameExists(false);
        } else {
            const exists = options?.some(existingOption => existingOption.name === productOption.name) || false;
            setNameExists(exists);
        }
    }, [productOption.name]);

    const handleChangeValue = useCallback((id: string, value: string) => {
        setProductOption(prev => ({
            ...prev,
            values: prev.values.map(v => v.id === id ? { ...v, value } : v)
        }));
        setModified(true);
    }, []);

    const handleDeleteValue = (id: string) => {
        setProductOption(prev => ({
            ...prev,
            values: prev.values.filter(v => v.id !== id)
        }));
    }

    const handleOnSave = () => {
        if (!modified) return;
        if (!productOption.name) {
            setValidationError("Name is required.");
            return;
        }
        if (productOption.values.length === 0) {
            setValidationError("At least one value is required.");
            return;
        }
        const optionInput: ProductOptionInput = {
            id: productOption.id,
            name: productOption.name,
            values: productOption.values.map(v => v.value),
            // Preserve the option's position when editing; append new options.
            position: option?.position ?? options.length,
        };
        onSave(optionInput);
    }

    const handleNameChange = (value: string) => {
        setProductOption(prev => ({
            ...prev,
            name: value
        }));
    };

    const reorder = (list: ValueObject[], startIndex: number, endIndex: number): ValueObject[] => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const dragSensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    );

    const handleDragEnd = ({ active, over }: DragEndEvent) => {
        if (!over || active.id === over.id) {
            return;
        }
        const oldIndex = productOption.values.findIndex(v => String(v.id) === String(active.id));
        const newIndex = productOption.values.findIndex(v => String(v.id) === String(over.id));
        if (oldIndex === -1 || newIndex === -1) {
            return;
        }
        const items = reorder(productOption.values, oldIndex, newIndex);
        setProductOption({ ...productOption, values: items });
    };

    return (
        <DialogPrimitive.Root
            open={open}
            onOpenChange={(nextOpen) => {
                if (!nextOpen) onClose();
            }}
        >
            <DialogPrimitive.Portal>
                <DialogPrimitive.Overlay className="productoptioneditor-overlay" />
                <div className="productoptioneditor-dialog">
                    <div className="productoptioneditor-dialog-container">
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
                            &#8203;
                        </span>
                        <DialogPrimitive.Content
                            className="relative productoptioneditor-container"
                            aria-describedby={undefined}
                        >
                            <div>
                                <div className="w-full productoptioneditor-layout">
                                    <div>
                                        <DialogPrimitive.Title asChild>
                                            <h3 className="productoptioneditor-title">
                                                {option?.id ? 'Edit Option' : 'Add Option'}
                                            </h3>
                                        </DialogPrimitive.Title>
                                        <div className="mt-4 space-y-1.5">
                                            <Label htmlFor="option-name">Name</Label>
                                            <Input
                                                id="option-name"
                                                data-testid="option-name-input"
                                                value={productOption.name}
                                                onChange={e => handleNameChange(e.target.value)}
                                                error={nameExists}
                                            />
                                            {nameExists && (
                                                <p className="text-sm text-destructive">Option name already exists.</p>
                                            )}
                                        </div>
                                        <div>
                                            <div className="mt-6 text-sm font-medium text-foreground">Values</div>
                                            {productOption.values.length > 0 && (
                                                <div className="productoptioneditor-values">
                                                    <DndContext sensors={dragSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                                                        <SortableContext items={productOption.values.map(v => String(v.id))} strategy={verticalListSortingStrategy}>
                                                            <div>
                                                                {productOption.values.map((value) => (
                                                                    <SortableValueRow
                                                                        key={value.id}
                                                                        value={value}
                                                                        error={errorValues.has(value.value)}
                                                                        onChange={handleChangeValue}
                                                                        onDelete={handleDeleteValue}
                                                                    />
                                                                ))}
                                                            </div>
                                                        </SortableContext>
                                                    </DndContext>
                                                </div>
                                            )}
                                            <div className="flex flex-row items-center mt-4 space-x-3">
                                                <div className="w-2/3">
                                                    <Input
                                                        aria-label="New value"
                                                        data-testid="option-value-input"
                                                        value={valueInput || ""}
                                                        onChange={e => setValueInput(e.target.value)}
                                                    />
                                                </div>
                                                <div>
                                                    <Button
                                                        variant="outline"
                                                        onClick={() => {
                                                            if (valueInput) {
                                                                setProductOption(prev => ({
                                                                    ...prev,
                                                                    values: [...prev.values, { id: uuid(), value: valueInput }]
                                                                }));
                                                                setValueInput("");
                                                            }
                                                        }}
                                                    >
                                                        Add
                                                    </Button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="productoptioneditor-footer">
                                    {validationError && (
                                        <div className="alert-container">
                                            <Alert variant="destructive" title="Validation Error">{validationError}</Alert>
                                        </div>
                                    )}
                                    <div className="button-container">
                                        <Button onClick={onClose} variant="outline">
                                            Close
                                        </Button>
                                        <Button onClick={handleOnSave} disabled={!modified || errorValues.size > 0 || nameExists}>
                                            Save
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </DialogPrimitive.Content>
                    </div>
                </div>
            </DialogPrimitive.Portal>
        </DialogPrimitive.Root>
    );
};
